import { useMemo } from 'react'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import ListSubheader from '@mui/material/ListSubheader'
import MenuItem from '@mui/material/MenuItem'
import MenuList from '@mui/material/MenuList'
import MyProfileIcon from '@mui/icons-material/BadgeOutlined'
import ManageBillingIcon from '@mui/icons-material/ReceiptLongOutlined'
import ManageVenuesIcon from '@mui/icons-material/StorefrontOutlined'
import CreateVenueIcon from '@mui/icons-material/AddBusinessOutlined'

import useAppStore from '$appStore'

import {
  BILLING_NAV_TARGET,
  BILLING_VENUES_NAV_TARGET,
  NEW_VENUE_NAV_TARGET,
  PROFILE_NAV_TARGET,
} from './navTargets'

type AccountSectionProps = {
  readonly onNavigate: (target: string) => void
}

/**
 * Account-scoped actions: the user's profile, and billing when the current user
 * owns at least one venue's subscription (only owners can manage billing).
 * A user with no venues at all (mid-signup) gets a Create a venue entry
 * instead, so they can always reach the venue creation flow.
 */
const AccountSection = ({ onNavigate }: AccountSectionProps) => {
  const venues = useAppStore((state) => state.venues)

  const isCurrentUserOwnerOfAVenue = useMemo(
    () => venues.some((venue) => venue.isSubscriptionOwner),
    [venues]
  )

  return (
    <MenuList>
      <ListSubheader sx={{ lineHeight: 2.5 }}>Account</ListSubheader>
      <MenuItem onClick={() => onNavigate(PROFILE_NAV_TARGET)}>
        <ListItemIcon>
          <MyProfileIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>My profile</ListItemText>
      </MenuItem>
      {isCurrentUserOwnerOfAVenue && (
        <>
          <MenuItem onClick={() => onNavigate(BILLING_NAV_TARGET)}>
            <ListItemIcon>
              <ManageBillingIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>My billing</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => onNavigate(BILLING_VENUES_NAV_TARGET)}>
            <ListItemIcon>
              <ManageVenuesIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>My venues</ListItemText>
          </MenuItem>
        </>
      )}
      {venues.length === 0 && (
        <MenuItem onClick={() => onNavigate(NEW_VENUE_NAV_TARGET)}>
          <ListItemIcon>
            <CreateVenueIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Create a venue</ListItemText>
        </MenuItem>
      )}
    </MenuList>
  )
}

export default AccountSection
